/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useCallback, useContext } from "react";
import { Logger } from "cms/utils/log/logger";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "cms/icons";
import SimpliCitySDK from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { DocumentEditContext } from "cms/documents/document-selector/context/document-edit-context";
import { DocumentDropArea } from "./document-drop-area";
import { DocumentUploadStatus } from "./replace-document-upload-status";
import { useUploadFile } from "./use-upload-file";
import { DEFAULT_UPLOAD_STATUS } from "cms/documents/document-selector/document-selector-config";

const { faRepeat } = solid;

const DocumentEditReplacePanel = () => {

    const [newFile, setNewFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploaded, setIsUploaded] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(DEFAULT_UPLOAD_STATUS);
    const { token } = useAuth();
    const { uploadFile } = useUploadFile();

    const {
        setIsReplacing,
        selectedDocument,
        setDocument,
        setSelectedDocument,
        onSelectDocument,
        setDocumentHandler,
        onReload,
        setHasChanges,
    } = useContext(DocumentEditContext);

    const onDocumentUploaded = async (replaced_document) => {
        try {
            const { guid } = selectedDocument;
            const response = await SimpliCitySDK.document.update(
                token, guid, replaced_document
            );
            Logger.debug(response);
            const purgeResponse = await SimpliCitySDK.document.purgeAll(token);
            Logger.debug(purgeResponse);
            Logger.debug("Document Replaced", selectedDocument);
            const updateDocument = {
                ...selectedDocument,
                ...response
            };
            setSelectedDocument(updateDocument);
            setNewFile(null);
            setIsUploaded(true);
            setIsUploading(false);
            setIsReplacing(false);
            setHasChanges(true);
            if(uploadStatus.status === "UPLOADED") {
                setDocument(selectedDocument);
                onSelectDocument(selectedDocument);
                setDocumentHandler(selectedDocument);
                onReload(selectedDocument);
            }
        } catch (error) {
            Logger.error(error);
        }
    };

    const handleUpload = useCallback(async () => {
        setIsUploading(true);
        const replacedFile = new File([newFile], selectedDocument?.file_name, { type: selectedDocument?.file_type });
        await uploadFile({
            document: selectedDocument,
            file: replacedFile,
            file_name: selectedDocument?.file_name,
            onDocumentUploaded,
            setUploadStatus
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newFile]);

    const handleCancel = useCallback(() => {
        setIsReplacing(false);
        setNewFile(null);
    }, [setIsReplacing]);

    return (
        <div className={`replace-section ${isUploaded ? "uploading" : ""}`}>
            <h2 className="sectionHeader">Replace document</h2>
            <p className="replace-confirmation">
                Select a replacement document with the same file type extension as the original 
                document.<br />
                It will be renamed to match the original document filename, and will overwrite the 
                original file.<br /><br/>
                <i>
                    <u>Note</u>: If you do not see the document updated right away after upload,
                    you may need to wait up to 15 mins to see the change due to caching at
                    the CDN and on browsers.
                </i>

            </p>
            <div className="file-type">
                <strong>Type: </strong>
                <span className="file-type-name">{selectedDocument.file_type} ({selectedDocument.file_ext})</span>
            </div>
            <DocumentDropArea
                newFile={newFile}
                setNewFile={setNewFile}
                setIsUploaded={setIsUploaded}
                fileType={selectedDocument?.file_type}
            />
            {newFile && (
                <DocumentUploadStatus
                    isUploading={isUploading}
                />
            )}
            <div className="replace-window-footer">
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleCancel}
                    className="cancel-btn"
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    className="replace-btn"
                    startIcon={<FontAwesomeIcon icon={faRepeat} />}
                    disabled={!isUploaded || isUploading}
                >
                    Replace
                </Button>
            </div>
        </div>
    );
};

export { DocumentEditReplacePanel };
